import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronDown, Download } from 'lucide-react';
import { StatTile } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/States';
import { DataNotes, InfoTip } from '../../components/ui/InfoTip';
import { RosterTab, isChurned, useRosterPopulation, type RosterPopulation } from './csm/RosterTab';
import { useCsmOverview, useCsmReconciliation } from '../../hooks/useDashboard';
import { AttioCompanyLink } from './csm/attio';
import { exportCsmOverview } from '../../api/dashboard';
import { formatDayMonthTime, formatDayMonthYear } from '../../utils/dateFormat';
import { formatMoney, formatNumber } from '../../utils/numberFormat';
import { formatMonthYear } from '../../utils/dateFormat';
import { shiftDaysISO, todayISO } from './csm/period';

const AUTH0_COVERAGE_DAYS = 90;
/** The KPI strip and the roster both describe the last 30 days. */
const WINDOW_DAYS = 30;
/** The export endpoint caps a page at 100 rows. */
const EXPORT_PAGE_SIZE = 100;

function downloadBlobFile(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function hasFullAuth0Coverage(value: string | null): boolean {
  if (!value) return false;
  const coverageStart = new Date(value);
  if (Number.isNaN(coverageStart.getTime())) return false;
  const requiredStart = new Date();
  requiredStart.setUTCDate(requiredStart.getUTCDate() - (AUTH0_COVERAGE_DAYS - 1));
  requiredStart.setUTCHours(0, 0, 0, 0);
  return coverageStart.getTime() <= requiredStart.getTime();
}

function syncedText(value: string | null | undefined): string {
  return value ? formatDayMonthTime(value) : 'never';
}

const METRIC_DEFINITIONS = (
  <div className="space-y-2">
    <p>
      <span className="font-medium text-text-primary">Customers</span>: the Attio CS roster below, with the same
      population switch applied (Customers, + Churned, Everyone), so the tiles and the table always count the same companies.
      “On the CSM list” is not Finance’s paying-customer count: the bracket says how many of them the MRR engine bills
      this month.
    </p>
    <p>
      <span className="font-medium text-text-primary">Active user</span>: an external member of a listed customer
      (not @sequesto.com) with at least one successful Auth0 login in the last {WINDOW_DAYS} days. Usage applies the
      same rule, so a company's members and active users read the same on both.
    </p>
    <p>
      <span className="font-medium text-text-primary">Active customer</span>: a listed customer with at least one
      active member in that window. <span className="font-medium text-text-primary">Inactive customers</span> leaves
      churned companies out; with churned shown, they get their own Churned tile.
    </p>
    <p>
      <span className="font-medium text-text-primary">MRR</span>: this month’s MRR from the Finance MRR engine (sheet
      overrides applied): the same number as the Finance tab. The tile's (i) splits it between the listed companies and
      the rest.
    </p>
  </div>
);

type PayingGap = NonNullable<NonNullable<ReturnType<typeof useCsmReconciliation>['data']>['payingNotOnList']>;

/**
 * One quiet line for paying companies the CSM list misses, opening to the rows
 * and the fix. It is a to-do for the list, not an outage, so it never takes over the page.
 */
function PayingGapBar({ gap }: { gap: PayingGap }) {
  const [open, setOpen] = useState(false);
  const total = gap.reduce((n, p) => n + (p.mrrEur ?? 0), 0);
  return (
    <div className="rounded-lg border border-warning/30 bg-warning-light/40 px-4 py-2.5 text-body-sm text-text-secondary">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left"
      >
        <AlertCircle className="h-4 w-4 flex-none text-warning" aria-hidden />
        <span className="flex-1">
          <span className="font-medium text-text-primary">
            {formatNumber(gap.length)} paying {gap.length === 1 ? 'company isn’t' : 'companies aren’t'} on the CSM list
          </span>
          {total > 0 && <span className="text-text-tertiary"> · {formatMoney(total)} MRR</span>}
        </span>
        <span className="flex items-center gap-1 text-body-xs text-text-tertiary">
          {open ? 'Hide' : 'Show'}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
        </span>
      </button>
      {open && (
        <div className="mt-2 pl-6">
          <p className="text-body-xs text-text-tertiary">
            Live in Odoo but not a live customer on the Attio CS master list. Add it to the list in Attio, or correct its
            stage if it isn’t churned.
          </p>
          <ul className="mt-1 divide-y divide-border-light">
            {gap.map((p) => (
              <li key={p.odooCompanyId} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                <div className="min-w-0">
                  <span className="text-text-primary">{p.attioName ?? p.odooName}</span>
                  {p.attioName && p.attioName !== p.odooName && (
                    <span className="ml-2 text-body-xs text-text-tertiary">{p.odooName}</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-body-xs text-text-tertiary">
                    {p.rosterStatus === 'churned' ? 'On the list as churned' : 'Not on the list'}
                  </span>
                  <span className="w-20 text-right tabular-nums">
                    {p.mrrEur === null ? <span title="A contract's currency couldn't be converted">unavailable</span> : formatMoney(p.mrrEur)}
                  </span>
                  <span className="inline-flex w-7 justify-end">
                    {p.attioRecordId && <AttioCompanyLink recordId={p.attioRecordId} name={p.attioName ?? p.odooName} />}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * CSM › Customers: the usage KPI strip on top of the Attio roster. Replaces the
 * separate Roster and Usage sub-tabs, which listed the same companies twice.
 */
export function CsmCustomers() {
  const range = useMemo(() => ({ from: shiftDaysISO(WINDOW_DAYS), to: todayISO() }), []);
  const overview = useCsmOverview({ ...range, status: 'all', search: '', page: 1, pageSize: 1 });
  const data = overview.data;
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<unknown>(null);
  const [population, setPopulation] = useState<RosterPopulation>('customers');
  // The KPI strip counts the same roster rows the table lists, so the
  // population switch moves both together.
  const { roster, rows: populationRows, population: effectivePopulation } = useRosterPopulation(population);
  const kpis = useMemo(() => {
    if (!roster.data) return null;
    const active = populationRows.filter((r) => r.activeMembers30d > 0);
    const activeUsers = populationRows.reduce((n, r) => n + r.activeMembers30d, 0);
    const allUsers = populationRows.reduce((n, r) => n + r.totalMembers, 0);
    const inView = new Set(populationRows.map((r) => r.entryId));
    const mrr = populationRows.reduce((n, r) => n + (r.mrr ?? 0), 0);
    // MRR the book counts on list entries outside this population (e.g. a churned entry still billing).
    const mrrOtherEntries = (roster.data.rows ?? []).filter((r) => !inView.has(r.entryId) && (r.mrr ?? 0) !== 0);
    return {
      mrr,
      mrrOtherEntries,
      total: populationRows.length,
      activeCompanies: active.length,
      // A churned company is not an inactive customer: it is counted on its own.
      inactiveCompanies: populationRows.filter((r) => r.activeMembers30d === 0 && !isChurned(r)).length,
      churnedCompanies: populationRows.filter(isChurned).length,
      activeUsers,
      inactiveUsers: Math.max(0, allUsers - activeUsers),
    };
  }, [roster.data, populationRows]);

  const reconciliation = useCsmReconciliation();
  const payingGap = reconciliation.data?.payingNotOnList ?? [];
  const book = roster.data?.mrr;

  const hasProdData = (data?.summary.totalCompanies ?? 0) > 0 || !!data?.lastProdSyncAt;
  const hasAuth0Data = !!data?.lastAuth0SyncAt;

  const notes: ReactNode[] = [];
  if (data && (data.summary.unmappedUsers ?? 0) > 0) {
    notes.push(
      <>
        {formatNumber(data.summary.unmappedUsers)} external users are not mapped to a company, so they count towards
        no customer.
      </>,
    );
  }
  if (data && hasProdData && !hasAuth0Data) {
    notes.push('Auth0 activity has not been synced yet, so activity counts and last-active dates stay empty.');
  }
  if (data && hasAuth0Data && !hasFullAuth0Coverage(data.auth0CoverageStartAt)) {
    notes.push(
      <>
        Auth0 history does not cover the full {AUTH0_COVERAGE_DAYS} days yet
        {data.auth0CoverageStartAt && <>; it starts {formatDayMonthYear(data.auth0CoverageStartAt)}</>}.
      </>,
    );
  }

  const handleExport = async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      const file = await exportCsmOverview({ ...range, status: 'all', page: 1, pageSize: EXPORT_PAGE_SIZE });
      downloadBlobFile(file.blob, file.fileName ?? 'csm-customers.xlsx');
    } catch (error) {
      setExportError(error);
    } finally {
      setIsExporting(false);
    }
  };

  // Unavailable (loading or failed) reads as a dash, never as zero.
  const value = (n: number | undefined) => (kpis && n != null ? formatNumber(n) : '—');
  // Names the population every tile counts. "Customers" is the CSM list, which is
  // not Finance's paying set, so it says how many of them pay:
  // "28 on the CSM list (26 paying)", "59 incl. churned", "64 companies". On 12 Oct 2026
  // 26 is right: 28 live customers on the list, less EMIS Health and StormHarvester, which
  // the MRR engine bills nothing for. Finance's book also carries Sligro (churned on the
  // list, still billing) and RCS Lab (not on the list), so it counts 28.
  const paying = populationRows.filter((r) => (r.mrr ?? 0) > 0).length;
  const populationLabel = kpis
    ? effectivePopulation === 'customers'
      ? `${formatNumber(kpis.total)} on the CSM list${book && !book.error ? ` (${formatNumber(paying)} paying)` : ''}`
      : `${formatNumber(kpis.total)} ${effectivePopulation === 'withChurned' ? 'incl. churned' : 'companies'}`
    : undefined;
  // The population is said once, in the strip's header; each tile's sub says what it counts.

  return (
    <div className="space-y-6">
      {overview.error && (
        <ErrorState title="Couldn’t load customer activity" error={overview.error} onRetry={() => overview.refetch()} />
      )}
      {data && !hasProdData && (
        <ErrorState
          title="Production data not synced yet"
          error="Run the Production DB Sync so companies, users and tokens are mirrored into the OS."
          details={
            <Link to="/integrations" className="underline">
              Open integrations
            </Link>
          }
        />
      )}
      {exportError != null && <ErrorState title="Export failed" error={exportError} compact />}

      {payingGap.length > 0 && <PayingGapBar gap={payingGap} />}
      {book?.error && (
        <ErrorState title="MRR unavailable: the Finance MRR book could not be built" error={book.error} compact />
      )}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 text-body-xs text-text-tertiary">
            <span className="text-body-sm font-medium text-text-secondary">
              Last {WINDOW_DAYS} days{populationLabel ? ` · ${populationLabel}` : ''}
            </span>
            <InfoTip label="Metric definitions">{METRIC_DEFINITIONS}</InfoTip>
            {data && (
              <span>
                Synced: production {syncedText(data.lastProdSyncAt)} · Auth0 {syncedText(data.lastAuth0SyncAt)} · CSM{' '}
                {syncedText(data.lastCsmRefreshAt)}
              </span>
            )}
            <DataNotes notes={notes} />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExport}
            isLoading={isExporting}
            disabled={!data || data.total === 0}
          >
            <Download className="mr-1.5 h-4 w-4" aria-hidden />
            Export
          </Button>
        </div>
        <div className={`grid gap-4 grid-cols-2 ${kpis && kpis.churnedCompanies > 0 ? 'xl:grid-cols-6' : 'xl:grid-cols-5'}`}>
          {/* Finance's MRR, so this tile and the Finance tab never disagree; the part on the CSM list is the sub line. */}
          <StatTile
            label="MRR"
            value={book && !book.error && book.total !== null ? formatMoney(book.total) : '—'}
            sub={
              kpis && book && !book.error && book.total !== null && book.total !== kpis.mrr
                ? `${formatMoney(kpis.mrr)} on the ${formatNumber(kpis.total)} listed`
                : book?.month
                  ? `${formatMonthYear(`${book.month}-01`)}, as on Finance`
                  : 'as on Finance'
            }
            info={
              kpis && book && !book.error && book.total !== null ? (
                <div className="space-y-1.5">
                  <p>
                    {formatMonthYear(`${book.month}-01`)} MRR from the Finance MRR engine: the same number as the Finance tab.
                  </p>
                  <p>
                    {formatMoney(kpis.mrr)} of it is on the {formatNumber(kpis.total)} companies counted here
                    {kpis.mrrOtherEntries.map((r) => (
                      <span key={r.entryId}>
                        {' '}+ {formatMoney(r.mrr ?? 0)} {r.companyName ?? 'unnamed'} ({isChurned(r) ? 'on the list as churned' : 'not in this view'})
                      </span>
                    ))}
                    {book.notOnRoster.map((n) => (
                      <span key={n.name}>
                        {' '}+ {formatMoney(n.mrr)} {n.name} (not on the CSM list)
                      </span>
                    ))}
                    .
                  </p>
                </div>
              ) : (
                'This month’s MRR from the Finance MRR engine, the same number as the Finance tab.'
              )
            }
          />
          <StatTile
            label="Active customers"
            value={value(kpis?.activeCompanies)}
            sub="a customer user logged in"
            tooltip="Roster companies with at least one member active in the last 30 days"
          />
          <StatTile
            label="Inactive customers"
            value={value(kpis?.inactiveCompanies)}
            sub="no customer user logged in"
            tooltip="Live roster customers with no member active in the last 30 days; churned companies are not counted here"
          />
          {kpis && kpis.churnedCompanies > 0 && (
            <StatTile label="Churned" value={value(kpis.churnedCompanies)} sub="on the list as churned" />
          )}
          <StatTile
            label="Active users"
            value={value(kpis?.activeUsers)}
            sub="external, with a login"
            tooltip="External members of the listed customers with a login in the last 30 days. SEQUESTO staff are left out, here and on Usage."
          />
          <StatTile
            label="Inactive users"
            value={value(kpis?.inactiveUsers)}
            sub="external, no login"
            tooltip="External members of the listed customers with no login in the last 30 days"
          />
        </div>
      </div>

      <RosterTab population={population} onPopulationChange={setPopulation} />
    </div>
  );
}
